// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type { WorkItemStatus } from '@web/lib/remoteWorkItemModel';
import {
  Check,
  CircleHelp,
  Clock3,
  Loader2,
  Pause,
  Timer,
  XCircle,
} from 'lucide';
import { useEffect, useState } from 'react';
import {
  durationToDateTime,
  formatDuration,
  isActiveWorkStatus,
  resolveDurationMs,
  WORK_STATUS_CLASS,
  WORK_STATUS_LABEL,
} from './presentation';

const CLOCK_UPDATE_INTERVAL_MS = 1000;

export interface TurnHeaderProps {
  status: WorkItemStatus;
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number | null;
  activeAgentCount?: number;
  itemCount?: number;
  className?: string;
}

export function useLiveDuration({
  status,
  startedAt,
  completedAt,
  durationMs,
}: Pick<
  TurnHeaderProps,
  'status' | 'startedAt' | 'completedAt' | 'durationMs'
>): number | null {
  const [now, setNow] = useState(() => Date.now());
  const active = isActiveWorkStatus(status);

  useEffect(() => {
    if (!active || !startedAt || completedAt) return;

    setNow(Date.now());
    const intervalId = window.setInterval(
      () => setNow(Date.now()),
      CLOCK_UPDATE_INTERVAL_MS
    );
    return () => window.clearInterval(intervalId);
  }, [active, completedAt, startedAt]);

  return resolveDurationMs({
    status,
    startedAt,
    completedAt,
    durationMs,
    now,
  });
}

function StatusGlyph({ status }: { status: WorkItemStatus }) {
  const className = 'h-3.5 w-3.5 shrink-0';
  switch (status) {
    case 'queued':
      return <MorphIcon icon={Clock3} aria-hidden className={className} />;
    case 'in_progress':
      return (
        <MorphIcon
          icon={Loader2}
          aria-hidden
          className={cn(className, 'animate-spin')}
        />
      );
    case 'waiting':
      return <MorphIcon icon={CircleHelp} aria-hidden className={className} />;
    case 'completed':
      return <MorphIcon icon={Check} aria-hidden className={className} />;
    case 'failed':
      return <MorphIcon icon={XCircle} aria-hidden className={className} />;
    case 'interrupted':
      return <MorphIcon icon={Pause} aria-hidden className={className} />;
  }
}

export function TurnHeader({
  status,
  startedAt,
  completedAt,
  durationMs,
  activeAgentCount = 0,
  itemCount,
  className,
}: TurnHeaderProps) {
  const elapsedMs = useLiveDuration({
    status,
    startedAt,
    completedAt,
    durationMs,
  });
  const durationLabel = formatDuration(elapsedMs);
  const dateTime = durationToDateTime(elapsedMs);

  return (
    <header
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2',
        className
      )}
      aria-label="Task run summary"
    >
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-label-xs font-semibold',
          WORK_STATUS_CLASS[status]
        )}
      >
        <StatusGlyph status={status} />
        {WORK_STATUS_LABEL[status]}
      </span>

      <span className="inline-flex items-center gap-1.5 text-label-xs tabular-nums text-ds-text-neutral-muted-default">
        <MorphIcon icon={Timer} aria-hidden className="h-3.5 w-3.5 shrink-0" />
        <time
          dateTime={dateTime}
          aria-label={
            elapsedMs == null
              ? 'Runtime unavailable'
              : `Runtime ${durationLabel}`
          }
        >
          {durationLabel}
        </time>
      </span>

      {activeAgentCount > 0 && (
        <span className="text-label-xs text-ds-text-neutral-muted-default">
          {activeAgentCount}{' '}
          {activeAgentCount === 1 ? 'agent working' : 'agents working'}
        </span>
      )}

      {itemCount != null && itemCount >= 0 && (
        <span className="text-label-xs text-ds-text-neutral-muted-default">
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
        </span>
      )}
    </header>
  );
}
